import {
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import React, {useCallback, useMemo, useRef, useState} from 'react';
import {useHistory, useLocation} from 'react-router-dom';

import {MaybeUserInfo} from '../../../../../../common/hooks/useViewerInfo';
import {safeLocalStorage} from '../../../../../../core/util/localStorage';
import {useDeepMemo} from '../../../../../../hookUtils';
import {getValidFilterModel} from '../../grid/filters';
import {getValidPaginationModel} from '../../grid/pagination';
import {getValidPinModel, removeAlwaysLeft} from '../../grid/pin';
import {getValidSortModel} from '../../grid/sort';
import {EVALUATE_OP_NAME_POST_PYDANTIC} from '../common/heuristics';
import {
  callsFilterToCallFilter,
  convertSortBysToGridSortModel,
  makeSavedViewLocalStorageKey,
  queryToGridFilterModel,
} from '../SavedViews/savedViewUtil';
import {useURLSearchParamsDict} from '../util';
import {
  ChartConfig,
  SavedViewDefinition,
} from '../wfReactInterface/generatedBuiltinObjectClasses.zod';
import {TraceObjSchema} from '../wfReactInterface/traceServerClientTypes';
import {opVersionKeyToRefUri} from '../wfReactInterface/utilities';
import {CallsPage} from './CallsPage';
import {
  ALWAYS_PIN_LEFT_CALLS,
  DEFAULT_PIN_CALLS,
  DEFAULT_SORT_CALLS,
  filterHasCalledAfterDateFilter,
} from './CallsTable';
import {WFHighLevelCallFilter} from './callsTableFilter';
import {
  convertLowLevelFilterToHighLevelFilter,
  DEFAULT_FILTER_CALLS,
  useMakeInitialDatetimeFilter,
} from './callsTableQuery';

type CallsPageLoadedProps = {
  entity: string;
  project: string;
  table: string;
  baseView: TraceObjSchema;
  fetchViews: () => void;
  views: TraceObjSchema[];
  userInfo: MaybeUserInfo;
};

export const CallsPageLoaded = ({
  entity,
  project,
  table,
  baseView,
  views,
  fetchViews,
  userInfo,
}: CallsPageLoadedProps) => {
  const location = useLocation();
  const currentViewerId = userInfo ? userInfo.id : null;
  const isReadonly = !currentViewerId || !userInfo?.teams.includes(entity);

  const onRecordLastView = useCallback(
    (loadedView: string) => {
      const localStorageKey = makeSavedViewLocalStorageKey(
        entity,
        project,
        table
      );
      safeLocalStorage.setItem(localStorageKey, loadedView);
    },
    [entity, project, table]
  );

  const viewDef = baseView.val.definition as SavedViewDefinition;
  const query = useURLSearchParamsDict();
  const isEvaluationsTab = table === 'evaluations';

  // For evals, create a frozen filter that only contains the op filter
  const frozenFilter: WFHighLevelCallFilter | undefined = useMemo(() => {
    if (!isEvaluationsTab) {
      return undefined;
    }
    return {
      opVersionRefs: [
        opVersionKeyToRefUri({
          entity,
          project,
          opId: EVALUATE_OP_NAME_POST_PYDANTIC,
          versionHash: '*',
        }),
      ],
    };
  }, [isEvaluationsTab, entity, project]);

  // Initial filter is the same for eval / traces: URL filter takes precedence over view definition
  const initialFilter: WFHighLevelCallFilter = useMemo(() => {
    if (query.filter !== undefined) {
      try {
        return JSON.parse(query.filter);
      } catch (e) {
        console.log(e);
        return {};
      }
    }
    return (
      convertLowLevelFilterToHighLevelFilter(
        callsFilterToCallFilter(viewDef.filter)
      ) ?? {}
    );
  }, [query.filter, viewDef.filter]);
  const history = useHistory();

  const {initialDatetimeFilter} = useMakeInitialDatetimeFilter(
    entity,
    project,
    initialFilter,
    isEvaluationsTab
  );

  const onFilterUpdate = useCallback(
    filter => {
      const newQuery = new URLSearchParams(location.search);
      newQuery.set('filter', JSON.stringify(filter));
      history.push({search: newQuery.toString()});
    },
    [history, location.search]
  );

  // Process url state for urls with hidden columns, this is legacy
  // We no longer persist column state to the URL, only to the saved view
  const columnVisibilityModelLegacy = useDeepMemo(
    useMemo(() => {
      if (query.cols) {
        // TODO: More validation on this query param
        try {
          return JSON.parse(query.cols);
        } catch (e) {
          console.log(e);
          return {};
        }
      }
      return viewDef.cols ?? {};
    }, [query.cols, viewDef])
  );
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(columnVisibilityModelLegacy);

  const pinModel = useDeepMemo(
    useMemo(() => {
      if (query.pin) {
        return getValidPinModel(
          query.pin,
          DEFAULT_PIN_CALLS,
          ALWAYS_PIN_LEFT_CALLS
        );
      }
      return viewDef.pin ?? {};
    }, [query.pin, viewDef])
  );
  const setPinModel = useCallback(
    (newModel: GridPinnedColumnFields) => {
      const newQuery = new URLSearchParams(location.search);
      newQuery.set(
        'pin',
        JSON.stringify(removeAlwaysLeft(newModel, ALWAYS_PIN_LEFT_CALLS))
      );
      history.push({search: newQuery.toString()});
    },
    [history, location.search]
  );

  // Track if the user has explicitly removed the date filter
  const hasRemovedDateFilter = useRef(false);

  // Only show the date filter if not evals and we haven't explicitly removed it
  const defaultFilter =
    isEvaluationsTab || hasRemovedDateFilter.current
      ? DEFAULT_FILTER_CALLS
      : initialDatetimeFilter;

  const filterModel = useDeepMemo(
    useMemo(() => {
      if (query.filters) {
        return getValidFilterModel(query.filters, defaultFilter);
      }
      return queryToGridFilterModel(viewDef.query) ?? defaultFilter;
    }, [query.filters, viewDef, defaultFilter])
  );
  const setFilterModel = useCallback(
    (newModel: GridFilterModel) => {
      // If there was a date filter and now there isn't, mark it as explicitly removed
      // so we don't add it back on subsequent navigations
      const hadDateFilter = filterHasCalledAfterDateFilter(filterModel);
      if (hadDateFilter && !filterHasCalledAfterDateFilter(newModel)) {
        hasRemovedDateFilter.current = true;
      }

      // When there are no items in the newModel, it would be tempting
      // to remove the query parameter entirely, however, that would cause
      // the value from the saved view to get used, which is a problem if you
      // just removed it.
      const newQuery = new URLSearchParams(location.search);
      newQuery.set('filters', JSON.stringify(newModel));
      history.push({search: newQuery.toString()});
    },
    [history, location.search, filterModel]
  );

  const sortModel: GridSortModel = useDeepMemo(
    useMemo(() => {
      if (query.sort) {
        return getValidSortModel(query.sort, DEFAULT_SORT_CALLS);
      }
      return (
        convertSortBysToGridSortModel(viewDef.sort_by) ?? DEFAULT_SORT_CALLS
      );
    }, [query.sort, viewDef])
  );
  const setSortModel = useCallback(
    (newModel: GridSortModel) => {
      const newQuery = new URLSearchParams(location.search);
      newQuery.set('sort', JSON.stringify(newModel));
      history.push({search: newQuery.toString()});
    },
    [history, location.search]
  );

  const paginationModel = useDeepMemo(
    useMemo(
      () =>
        getValidPaginationModel(
          query.page,
          query.pageSize ?? viewDef.page_size
        ),
      [query.page, query.pageSize, viewDef]
    )
  );
  const setPaginationModel = useCallback(
    (newModel: GridPaginationModel) => {
      const newQuery = new URLSearchParams(location.search);
      const {page, pageSize} = newModel;
      // TODO: If we change page size, should we reset page to 0?
      if (page === 0) {
        newQuery.delete('page');
      } else {
        newQuery.set('page', page.toString());
      }
      newQuery.set('pageSize', pageSize.toString());
      history.push({search: newQuery.toString()});
    },
    [history, location.search]
  );

  const expandRefCols = useDeepMemo(
    useMemo(() => {
      if (query.expandRefCols) {
        try {
          const parsed = JSON.parse(query.expandRefCols);
          return Array.isArray(parsed) ? new Set(parsed) : new Set<string>();
        } catch (e) {
          console.log(e);
          return new Set<string>();
        }
      }
      return new Set<string>();
    }, [query.expandRefCols])
  );

  const setExpandRefCols = useCallback(
    (newModel: Set<string>) => {
      const newQuery = new URLSearchParams(location.search);
      const colsArray = Array.from(newModel);

      // Only include expand columns in URL if there are any expanded columns
      if (colsArray.length > 0) {
        newQuery.set('expandRefCols', JSON.stringify(colsArray));
      } else {
        newQuery.delete('expandRefCols');
      }

      history.push({search: newQuery.toString()});
    },
    [history, location.search]
  );

  const charts = useDeepMemo(
    useMemo(() => {
      if (query.charts) {
        try {
          return JSON.parse(query.charts) as ChartConfig[];
        } catch (e) {
          console.log(e);
          return viewDef.charts ?? null;
        }
      }
      return viewDef.charts ?? null;
    }, [query.charts, viewDef])
  );

  const setCharts = useCallback(
    (newCharts: ChartConfig[] | null) => {
      // Normalize charts for comparison (remove undefined values, sort keys)
      const normalizeChart = (chart: ChartConfig) => {
        const normalized: any = {};
        // Sort keys and filter out undefined values
        Object.keys(chart)
          .sort()
          .forEach(key => {
            const value = (chart as any)[key];
            if (value !== undefined) {
              normalized[key] = value;
            }
          });
        return normalized;
      };

      const normalizeCharts = (chartsArray: ChartConfig[] | null) => {
        if (!chartsArray) return null;
        return chartsArray.map(normalizeChart);
      };

      // Only update if charts actually changed
      const currentChartsNormalized = JSON.stringify(normalizeCharts(charts));
      const newChartsNormalized = JSON.stringify(normalizeCharts(newCharts));

      if (currentChartsNormalized === newChartsNormalized) {
        return; // No change, don't update URL
      }

      const newQuery = new URLSearchParams(location.search);
      if (newCharts && newCharts.length > 0) {
        newQuery.set('charts', JSON.stringify(newCharts));
      } else {
        newQuery.delete('charts');
      }
      history.push({search: newQuery.toString()});
    },
    [history, location.search, charts]
  );

  const chartsOpen = useMemo(() => {
    if (query.chartsOpen === 'false') {
      return false;
    }
    if (query.chartsOpen === 'true') {
      return true;
    }
    // Default to closed for both traces and evaluations
    return false;
  }, [query.chartsOpen]);

  const setChartsOpen = useCallback(
    (open: boolean) => {
      const newQuery = new URLSearchParams(location.search);
      if (open) {
        newQuery.set('chartsOpen', 'true');
      } else {
        newQuery.delete('chartsOpen');
      }
      history.replace({search: newQuery.toString()});
    },
    [history, location.search]
  );

  const datasetSelector = useMemo(() => {
    if (query.datasetSelector !== undefined) {
      try {
        return JSON.parse(query.datasetSelector);
      } catch (e) {
        console.error('Failed to parse datasetSelector:', e);
        return query.datasetSelector;
      }
    }
    return viewDef.dataset_selector ?? null;
  }, [query.datasetSelector, viewDef.dataset_selector]);

  const setDatasetSelector = useCallback(
    (selector: string | null) => {
      const newQuery = new URLSearchParams(location.search);
      // Always set the param (even if null) to override saved view default
      newQuery.set('datasetSelector', JSON.stringify(selector));
      history.push({search: newQuery.toString()});
    },
    [history, location.search]
  );

  const evaluationSelector = useMemo(() => {
    if (query.evaluationSelector !== undefined) {
      try {
        return JSON.parse(query.evaluationSelector);
      } catch (e) {
        console.error('Failed to parse evaluationSelector:', e);
        return query.evaluationSelector;
      }
    }
    return viewDef.evaluation_selector ?? null;
  }, [query.evaluationSelector, viewDef.evaluation_selector]);

  const setEvaluationSelector = useCallback(
    (selector: string | null) => {
      const newQuery = new URLSearchParams(location.search);
      // Always set the param (even if null) to override saved view default
      newQuery.set('evaluationSelector', JSON.stringify(selector));
      history.push({search: newQuery.toString()});
    },
    [history, location.search]
  );

  const traceSearchQuery = useMemo(() => {
    return query.traceSearchQuery ?? '';
  }, [query.traceSearchQuery]);

  const setTraceSearchQuery = useCallback(
    (searchQuery: string) => {
      const newQuery = new URLSearchParams(location.search);
      if (searchQuery !== '') {
        newQuery.set('traceSearchQuery', searchQuery);
      } else {
        newQuery.delete('traceSearchQuery');
      }
      history.push({search: newQuery.toString()});
    },
    [history, location.search]
  );

  const traceSearchStrict = useMemo(() => {
    return query.traceSearchStrict === '1';
  }, [query.traceSearchStrict]);

  const setTraceSearchStrict = useCallback(
    (strict: boolean) => {
      const newQuery = new URLSearchParams(location.search);
      if (strict) {
        newQuery.set('traceSearchStrict', '1');
      } else {
        newQuery.delete('traceSearchStrict');
      }
      history.push({search: newQuery.toString()});
    },
    [history, location.search]
  );

  const headerDepth = useMemo(() => {
    if (query.headerDepth !== undefined) {
      const parsed = parseInt(query.headerDepth, 10);
      if (!isNaN(parsed) && parsed >= 1 && parsed <= 8) {
        return parsed;
      }
    }
    return viewDef.header_depth ?? (isEvaluationsTab ? 4 : 2);
  }, [query.headerDepth, viewDef.header_depth, isEvaluationsTab]);

  const setHeaderDepth = useCallback(
    (depth: number) => {
      const newQuery = new URLSearchParams(location.search);
      newQuery.set('headerDepth', depth.toString());
      history.push({search: newQuery.toString()});
    },
    [history, location.search]
  );

  return (
    <CallsPage
      currentViewerId={currentViewerId}
      isReadonly={isReadonly}
      entity={entity}
      project={project}
      baseView={baseView}
      views={views}
      fetchViews={fetchViews}
      onRecordLastView={onRecordLastView}
      frozenFilter={frozenFilter}
      initialFilter={initialFilter}
      onFilterUpdate={onFilterUpdate}
      columnVisibilityModel={columnVisibilityModel}
      setColumnVisibilityModel={setColumnVisibilityModel}
      pinModel={pinModel}
      setPinModel={setPinModel}
      filterModel={filterModel}
      setFilterModel={setFilterModel}
      sortModel={sortModel}
      setSortModel={setSortModel}
      paginationModel={paginationModel}
      setPaginationModel={setPaginationModel}
      expandRefCols={expandRefCols}
      setExpandRefCols={setExpandRefCols}
      charts={charts}
      setCharts={setCharts}
      chartsOpen={chartsOpen}
      setChartsOpen={setChartsOpen}
      datasetSelector={datasetSelector}
      setDatasetSelector={setDatasetSelector}
      evaluationSelector={evaluationSelector}
      setEvaluationSelector={setEvaluationSelector}
      traceSearchQuery={traceSearchQuery}
      setTraceSearchQuery={setTraceSearchQuery}
      traceSearchStrict={traceSearchStrict}
      setTraceSearchStrict={setTraceSearchStrict}
      headerDepth={headerDepth}
      setHeaderDepth={setHeaderDepth}
    />
  );
};
